import VerticalLayout from "../../../../../lib/html5-software-library/src/ranj/components/layouts/VerticalLayout";
import ScrollPane from "../../../../../lib/html5-software-library/src/ranj/components/scrolling/ScrollPane";
import Rectangle from "../../../../../lib/html5-software-library/src/ranj/geom/Rectangle";
import AbstractMediator from "../../../core/ui/AbstractMediator";
import FileModel from "../model/FileModel";
import LayoutManager from "../../../../../lib/html5-software-library/src/ranj/layout/LayoutManager";
import ClickEaterFactory from "../../../core/ui/ClickEaterFactory";
import RevisableLanguagePack from "../../../../../lib/html5-software-library/src/ranj/language/RevisableLanguagePack";
import FileExplorerModel from "../model/FileExplorerModel";
import FileExplorerModelEvent from "../model/FileExplorerModelEvent";
import FileExplorerItemFactory from "../controller/FileExplorerItemFactory";
import FileInteractionEvent from "../model/FileInteractionEvent";
import FileModelEvent from "../event/FileModelEvent";

export default class FileExplorerMediator extends AbstractMediator {
    /**
     * @constructor
     * @param {createjs.MovieClip} target 
     * @param {soma.EventDispatcher} dispatcher 
     * @param {LayoutManager} layoutManager 
     * @param {ClickEaterFactory} clickEaterFactory 
     * @param {FileModel} fileModel 
     * @param {FileExplorerModel} fileExplorerModel 
     * @param {FileExplorerItemFactory} fileExplorerItemFactory 
     * @param {RevisableLanguagePack} languagePack 
     */
    constructor(target, dispatcher, layoutManager, clickEaterFactory, fileModel, fileExplorerModel, fileExplorerItemFactory, languagePack) {
        super(target, dispatcher, layoutManager, clickEaterFactory);

        this._fileModel = fileModel;
        this._fileExplorerModel = fileExplorerModel;
        this._fileExplorerItemFactory = fileExplorerItemFactory;
        this._languagePack = languagePack;

        this._skinKeyRegistry = this.getCustomData().skinKeyRegistry;

        this._items = [];
    }

    initialize() {
        super.initialize();

        this._pathTextField = this._target.pathTextField;

        this._itemContainer = new VerticalLayout(this._target.scrollPane.itemContainer);

        const scrollPaneBounds = this._target.scrollPane.nominalBounds;
        const viewport = new Rectangle(0, 0, scrollPaneBounds.width, scrollPaneBounds.height);
        const halvedDragButtonHeight = this._target.scrollBar.dragButton.nominalBounds.height * .5;
        this._scrollPane = new ScrollPane(viewport, null, this._target.scrollBar, this._target.scrollPane.itemContainer, null, true, true, halvedDragButtonHeight);

        this._backButton = this.createButton(this._target.backButton, this._handleBackButton.bind(this));

        this._target.titleTextField.text = this._languagePack.getText(`fileExplorer.applicationTitle`);
        this._target.columnHeader1TextField.text = this._languagePack.getText(`fileExplorer.nameColumnLabel`);

        this._updateItems();
        this._updateBackButton();
        this._pathTextField.text = "/";

        this.registerEventListener(FileInteractionEvent.CLOSE_FILE, this._handleCloseFile.bind(this));
        this.registerEventListener(FileExplorerModelEvent.TREE_CHANGED, this._update.bind(this));
        this.registerEventListener(FileModelEvent.FILE_ADDED, this._update.bind(this));
        this.registerEventListener(FileModelEvent.FILE_REMOVED, this._update.bind(this));

        this._target.noFilesTextField.text = this._languagePack.getText(`fileExplorer.noFilesInDirectory`);
        this._target.noFilesTextField.visible = !this._directoryHasFiles();
    }

    destroy() {
        super.destroy();

        this._destroyItems();

        if (this._scrollPane) {
            this._scrollPane.destroy();
            this._scrollPane = null;
        }
    }

    _handleBackButton() {
        this._fileExplorerModel.currentFileTreeNode = this._fileExplorerModel.currentFileTreeNode.parent;
    }

    _handleCloseButton() {
        this._close();
    }

    _update() {
        this._pathTextField.text = this._fileExplorerModel.getCurrentPath();
        this._updateBackButton();
        this._updateItems();
    }

    _handleCloseFile() {
        this._updateScrollPane();
    }

    _updateScrollPane() {
        const contentBounds = this._itemContainer.getBounds();
        let contentHeight = contentBounds ? contentBounds.height : 0;
        this._scrollPane.setHeightBound(contentHeight);
        this._scrollPane.setScrollPosition(0, 0);
    }

    _updateBackButton() {
        this._backButton.setEnabled(!!this._fileExplorerModel.currentFileTreeNode.parent);
    }

    _updateItems() {
        this._destroyItems();

        const fileTreeNode = this._fileExplorerModel.currentFileTreeNode;
        for (let i = 0; i < fileTreeNode.children.length; i++) {
            const childNode = fileTreeNode.children[i];
            const item = this._fileExplorerItemFactory.createItem(childNode, this._skinKeyRegistry);
            this._itemContainer.addItem(item.skin);
            this._items.push(item);
        }

        this._updateScrollPane();
    }

    _destroyItems() {
        for (let i = 0; i < this._items.length; i++) {
            const item = this._items[i];
            this._itemContainer.removeItem(item.skin);
            item.destroy();
        }
    }

    /**
     * Whether the current directory has files in it or not.
     * @returns {boolean}
     */
    _directoryHasFiles() {
        const fileTreeNode = this._fileExplorerModel.currentFileTreeNode;
        return fileTreeNode.children.length > 0;
    }
}